// Edit Staff: imports and dependencies
import AdminActionIcon from "./AdminActionIcon";
import { useToastMessageState } from "../components/Toast/Toast";
import React, { useState, useEffect } from "react";
import { API_BASE_URL } from "../config";
import { isValidEmail } from "../utils/email";
import Modal from "./Modal";
import ProfilePictureInput from "./ProfilePictureInput";
import "./AddStaff.css";
import StaffDetailSections, {
  staffDetailValues,
  appendStaffDetails,
  validateStaffDetails,
  StaffDetailRecord,
} from "./StaffDetailSections";
import { GENDER_OPTIONS } from "../utils/gender";
import { EMPLOYMENT_TYPES } from "../utils/employmentTypes";

// Data types and contracts
interface Document {
  documentId: number;
  documentName: string;
  documentURL: string;
}

interface Staff extends StaffDetailRecord {
  id: number;
  name: string;
  email: string;
  phone: string;
  dob: string;
  genderCode?: string | null;
  doj: string;
  roleId: number;
  roleName: string;
  employmentType?: string | null;
  schoolName: string;
  address: string;
  isActive: boolean;
  profilePictureUrl?: string | null;
  documents: Document[];
}

interface EditStaffProps {
  isOpen: boolean;
  onClose: () => void;
  staff: Staff | null;
  onSuccess: () => void;
}

// Main component and state
const EditStaff: React.FC<EditStaffProps> = ({
  isOpen,
  onClose,
  staff,
  onSuccess,
}) => {
  const [profilePicture, setProfilePicture] = useState<File | null>(null);
  const [, setSaveError] = useToastMessageState("error");
  const [saving, setSaving] = useState(false);
  const [currentPictureUrl, setCurrentPictureUrl] = useState<string | null>(
    null,
  );
  const [formData, setFormData] = useState({
    name: "",
    dob: "",
    genderCode: "",
    doj: "",
    roleId: 0,
    employmentType: "",
    email: "",
    phone: "",
    ...staffDetailValues(),
    isActive: true,
  });

  const [newDocuments, setNewDocuments] = useState<
    Array<{ name: string; file: File }>
  >([]);
  const [existingDocuments, setExistingDocuments] = useState<
    Array<{
      id: number;
      name: string;
      url: string;
      originalName: string;
      newFile?: File;
    }>
  >([]);

  useEffect(() => {
    if (isOpen && staff) {
      console.log("Staff data:", staff);
      console.log("Documents:", staff.documents);
      setFormData({
        name: staff.name,
        dob: staff.dob.split("T")[0],
        genderCode: staff.genderCode || "",
        doj: staff.doj.split("T")[0],
        roleId: staff.roleId,
        employmentType: staff.employmentType || "",
        email: staff.email,
        phone: staff.phone,
        ...staffDetailValues(staff),
        isActive: staff.isActive,
      });
      setExistingDocuments(
        staff.documents.map((doc) => ({
          id: doc.documentId,
          name: doc.documentName,
          url: doc.documentURL,
          originalName: doc.documentName, // Store original name to track changes
        })),
      );
      setProfilePicture(null);
      setSaveError("");
      setCurrentPictureUrl(staff.profilePictureUrl || null);
      setNewDocuments([]);
    }
  }, [isOpen, staff]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const detailError = validateStaffDetails(formData);
    if (!isValidEmail(formData.email)) { setSaveError("Enter a valid staff email address."); return; }
    if (detailError) {
      setSaveError(detailError);
      return;
    }
    if (!staff || saving) return;
    setSaving(true);
    setSaveError("");

    try {
      const token = localStorage.getItem("token");
      const formDataToSend = new FormData();
      if (profilePicture)
        formDataToSend.append("ProfilePicture", profilePicture);

      formDataToSend.append("Id", staff.id.toString());
      formDataToSend.append("Name", formData.name);
      formDataToSend.append("Email", formData.email);
      formDataToSend.append("Phone", formData.phone);
      formDataToSend.append("Address", formData.address);
      appendStaffDetails(formDataToSend, formData);
      formDataToSend.append("DOB", new Date(formData.dob).toISOString());
      formDataToSend.append("GenderCode", formData.genderCode);
      formDataToSend.append("DOJ", new Date(formData.doj).toISOString());
      formDataToSend.append("RoleId", formData.roleId.toString());
      formDataToSend.append("EmploymentType", formData.employmentType);
      formDataToSend.append("IsActive", formData.isActive.toString());

      // Prepare arrays for proper alignment
      const documentIds: string[] = [];
      const documentNames: string[] = [];
      const files: File[] = [];

      // Add only changed existing documents
      existingDocuments.forEach((doc) => {
        const hasNameChanged = doc.name !== doc.originalName;
        const hasFileChanged = doc.newFile !== undefined;

        // Only include if name or file has changed
        if (hasNameChanged || hasFileChanged) {
          documentIds.push(doc.id.toString());
          documentNames.push(doc.name);
          if (doc.newFile) {
            files.push(doc.newFile);
          } else {
            // If only name changed, send empty file
            const emptyFile = new File([], "", {
              type: "application/octet-stream",
            });
            files.push(emptyFile);
          }
        }
      });

      // Add new documents (they don't have IDs, so no DocumentIds for these)
      newDocuments.forEach((doc) => {
        if (doc.file && doc.name.trim()) {
          // For new documents, we don't append DocumentIds (they don't exist yet)
          documentNames.push(doc.name);
          files.push(doc.file);
        }
      });

      // Append to FormData in correct order
      documentIds.forEach((id) => formDataToSend.append("DocumentIds", id));
      documentNames.forEach((name) =>
        formDataToSend.append("DocumentNames", name),
      );
      files.forEach((file) => formDataToSend.append("Files", file));

      // Debug logging
      const changedExistingDocs = existingDocuments.filter(
        (doc) => doc.name !== doc.originalName || doc.newFile !== undefined,
      );
      const validNewDocs = newDocuments.filter(
        (doc) => doc.file && doc.name.trim(),
      );

      console.log("=== UPDATE STAFF DOCUMENTS ===");
      console.log("Changed existing documents:", changedExistingDocs.length);
      console.log("New documents being added:", validNewDocs.length);
      console.log("Total DocumentIds sent:", documentIds.length);
      console.log("Total DocumentNames sent:", documentNames.length);
      console.log("Total Files sent:", files.length);
      console.log("DocumentIds:", documentIds);
      console.log("DocumentNames:", documentNames);
      console.log(
        "Files:",
        files.map((f) => f.name || "empty"),
      );
      console.log("===============================");

      const response = await fetch(`${API_BASE_URL}/api/Admin/update-staff`, {
        method: "PUT",
        headers: {
          accept: "*/*",
          Authorization: `Bearer ${token}`,
        },
        body: formDataToSend,
      });
      const result = await response.json();
      if (!response.ok || result.success === false)
        throw new Error(result.message || "Failed to update staff");
      if (response.ok) {
        onSuccess();
        onClose();
      }
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : "Failed to update staff",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleAddDocument = () => {
    setNewDocuments([...newDocuments, { name: "", file: null as any }]);
  };

  const handleRemoveNewDocument = (index: number) => {
    setNewDocuments(newDocuments.filter((_, i) => i !== index));
  };

  const handleExistingDocNameChange = (index: number, name: string) => {
    const updated = [...existingDocuments];
    updated[index].name = name;
    setExistingDocuments(updated);
  };

  const handleExistingDocFileChange = (index: number, file: File | null) => {
    if (file) {
      const updated = [...existingDocuments];
      updated[index].newFile = file;
      setExistingDocuments(updated);
    }
  };

  const handleDocumentNameChange = (index: number, name: string) => {
    const updated = [...newDocuments];
    updated[index].name = name;
    setNewDocuments(updated);
  };

  const handleDocumentFileChange = (index: number, file: File | null) => {
    if (file) {
      const updated = [...newDocuments];
      updated[index].file = file;
      setNewDocuments(updated);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (!saving) onClose();
      }}
      submitLoading={saving}
      loadingText="Updating..."
      title="Edit Staff"
      submitLabel="Update Staff"
      onCancel={() => {}}
      showCancel={false}
      formId="edit-staff-form"
    >
      <form id="edit-staff-form" onSubmit={handleSubmit}>
        <ProfilePictureInput
          id="edit-staff-picture"
          currentUrl={currentPictureUrl}
          file={profilePicture}
          onChange={setProfilePicture}
        />
        <div className="form-grid">
          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label>Email *</label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label>Phone *</label>
            <input
              type="tel"
              required
              maxLength={10}
              pattern="[0-9]{10}"
              value={formData.phone}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, "");
                if (value.length <= 10) {
                  setFormData({ ...formData, phone: value });
                }
              }}
            />
          </div>
          <div className="form-group">
            <label>Date of Birth *</label>
            <input
              type="date"
              required
              value={formData.dob}
              onChange={(e) =>
                setFormData({ ...formData, dob: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label>Gender *</label>
            <select
              required
              value={formData.genderCode}
              onChange={(e) =>
                setFormData({ ...formData, genderCode: e.target.value })
              }
            >
              <option value="">Select Gender</option>
              {GENDER_OPTIONS.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Date of Joining *</label>
            <input
              type="date"
              required
              value={formData.doj}
              onChange={(e) =>
                setFormData({ ...formData, doj: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label>Role</label>
            <input value={staff?.roleName || ""} readOnly />
            <small>
              Use Promote or Demote on the staff profile to change this role.
            </small>
          </div>
          <div className="form-group">
            <label>Employment Type *</label>
            <select
              required
              value={formData.employmentType}
              onChange={(e) =>
                setFormData({ ...formData, employmentType: e.target.value })
              }
            >
              <option value="">Select Employment Type</option>
              {EMPLOYMENT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Status</label>
            <div className="toggle-container">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                />
                <span className="toggle-slider"></span>
              </label>
              <span
                className={`toggle-label ${formData.isActive ? "active" : "inactive"}`}
              >
                {formData.isActive ? "Active" : "Inactive"}
              </span>
            </div>
          </div>
        </div>

        <StaffDetailSections
          values={formData}
          disabled={saving}
          onChange={(key, value) =>
            setFormData((current) => ({ ...current, [key]: value }))
          }
        />

        <div className="documents-section">
          <div className="documents-header">
            <label>Existing Documents ({existingDocuments.length})</label>
          </div>
          {existingDocuments.length === 0 ? (
            <p style={{ padding: "12px", color: "#718096", fontSize: "14px" }}>
              No existing documents
            </p>
          ) : (
            existingDocuments.map((doc, index) => (
              <div key={doc.id} className="existing-doc-row-edit">
                <input
                  type="text"
                  value={doc.name}
                  onChange={(e) =>
                    handleExistingDocNameChange(index, e.target.value)
                  }
                  placeholder="Document Name"
                />
                <div className="file-input-wrapper">
                  <input
                    type="file"
                    id={`existing-file-${index}`}
                    onChange={(e) =>
                      handleExistingDocFileChange(
                        index,
                        e.target.files?.[0] || null,
                      )
                    }
                    style={{ display: "none" }}
                  />
                  <label
                    htmlFor={`existing-file-${index}`}
                    className="file-input-label"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7,10 12,15 17,10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    {doc.newFile ? doc.newFile.name : "Upload"}
                  </label>
                </div>
                <a
                  href={`${API_BASE_URL}${doc.url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-view-existing"
                  onClick={(e) => {
                    console.log("View clicked:", `${API_BASE_URL}${doc.url}`);
                  }}
                >
                  View
                </a>
              </div>
            ))
          )}
        </div>

        <div className="documents-section">
          <div className="documents-header">
            <label>Add New Documents</label>
            <button
              type="button"
              className="btn-add-doc"
              onClick={handleAddDocument}
            >
              <AdminActionIcon action="add" />
              Add Document
            </button>
          </div>
          {newDocuments.map((doc, index) => (
            <div key={index} className="new-document-row">
              <input
                type="text"
                placeholder="Document Name"
                value={doc.name}
                onChange={(e) =>
                  handleDocumentNameChange(index, e.target.value)
                }
              />
              <div className="file-input-wrapper">
                <input
                  type="file"
                  id={`new-file-${index}`}
                  onChange={(e) =>
                    handleDocumentFileChange(index, e.target.files?.[0] || null)
                  }
                  style={{ display: "none" }}
                />
                <label
                  htmlFor={`new-file-${index}`}
                  className="file-input-label"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7,10 12,15 17,10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  {doc.file ? doc.file.name : "Upload"}
                </label>
              </div>
              <button
                type="button"
                className="btn-remove"
                onClick={() => handleRemoveNewDocument(index)}
                aria-label="Remove document"
              >
                <AdminActionIcon action="delete" />
              </button>
            </div>
          ))}
        </div>
      </form>
    </Modal>
  );
};

export default EditStaff;
